import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  where,
  writeBatch,
} from 'firebase/firestore';
import type { Workspace, WorkspaceRecordsCount } from '../types/workspaces';

const WORKSPACES_COLLECTION = 'workspaces';

export async function getWorkspaces(pageSize?: number, lastDoc?: QueryDocumentSnapshot): Promise<{ workspaces: Workspace[]; lastDoc?: QueryDocumentSnapshot }> {
  console.log('[API] getWorkspaces called', { pageSize, lastDoc });
  const workspacesRef = collection(db, WORKSPACES_COLLECTION);
  let q = query(workspacesRef);
  if (pageSize) {
    q = query(q, limit(pageSize));
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
  }
  const snapshot = await getDocs(q);
  const workspaces = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Workspace));
  const newLastDoc = snapshot.docs[snapshot.docs.length - 1];
  return {
    workspaces,
    lastDoc: newLastDoc
  };
}

export async function getWorkspaceById(id: string): Promise<Workspace | null> {
  const ref = doc(db, WORKSPACES_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return { id: snap.id, ...data } as Workspace;
}

export async function addWorkspace(workspace: Omit<Workspace, 'id'>): Promise<string> {
  console.log('[API] addWorkspace called', { workspace });
  console.log('[API] Language:', workspace.language);
  console.log('[API] Currency:', workspace.currency);
  const ref = doc(collection(db, WORKSPACES_COLLECTION));
  await setDoc(ref, workspace);
  return ref.id;
}

export async function updateWorkspace(id: string, data: Partial<Workspace>): Promise<void> {
  console.log('[API] updateWorkspace called', { id, data });
  console.log('[API] Language:', data.language);
  console.log('[API] Currency:', data.currency);
  const ref = doc(db, WORKSPACES_COLLECTION, id);
  await updateDoc(ref, data);
}

export async function deleteWorkspace(id: string): Promise<void> {
  console.log('[API] deleteWorkspace called', { id });
  const ref = doc(db, WORKSPACES_COLLECTION, id);
  
  try {
    // First check if the workspace exists
    const workspaceDoc = await getDoc(ref);
    if (!workspaceDoc.exists()) {
      console.log('[API] Workspace not found');
      return;
    }

    // Delete the workspace
    await deleteDoc(ref);
    console.log('[API] Workspace deleted successfully');
  } catch (error) {
    console.error('[API] Error deleting workspace:', error);
    throw new Error('Failed to delete workspace: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

/**
 * Calculate and update records count for a workspace
 * @param workspaceId - The ID of the workspace
 * @returns Promise with updated records count
 */
export const calculateAndUpdateWorkspaceRecordsCount = async (workspaceId: string): Promise<WorkspaceRecordsCount> => {
  const collections = [
    'apartment_units', 'assets', 'attendance', 'blocks', 'calendar_events', 'chatThreads',
    'customers', 'devices', 'employees', 'guidelines', 'incidents', 'invitations',
    'leads', 'leave_requests', 'lost_and_found', 'maintenance_requests', 'news',
    'notifications', 'packages', 'parkings', 'payables', 'payrolls', 'price_plans',
    'prospectTags', 'receivables', 'reservations', 'residents', 'support_tickets',
    'tasks', 'users', 'vendors', 'visitors'
  ];

  const recordsCount: Partial<WorkspaceRecordsCount> = {};
  let totalRecords = 0;

  // Create all queries in parallel - this is much faster than sequential
  const queryPromises = collections.map(async (collectionName) => {
    try {
      const collectionRef = collection(db, collectionName);
      
      // Query by workspace.id only - no fallback needed
      const q = query(collectionRef, where('workspace.id', '==', workspaceId));
      const snapshot = await getDocs(q);
      const count = snapshot.size;
      
      return { collectionName, count };
    } catch (error) {
      console.error(`Error counting ${collectionName}:`, error);
      return { collectionName, count: 0 };
    }
  });

  // Execute all queries in parallel - this reduces total time significantly
  const results = await Promise.all(queryPromises);

  // Process results
  results.forEach(({ collectionName, count }) => {
    (recordsCount as any)[collectionName] = count;
    totalRecords += count;
  });

  const finalRecordsCount: WorkspaceRecordsCount = {
    ...recordsCount,
    lastUpdated: new Date().toISOString()
  } as WorkspaceRecordsCount;

  // Update the workspace document with the new records count
  console.log(`Updating records count for workspace ${workspaceId}`);
  const workspaceRef = doc(db, 'workspaces', workspaceId);
  await updateDoc(workspaceRef, {
    records_count: finalRecordsCount
  });

  return finalRecordsCount;
};


/**
 * Comprehensive workspace deletion function that removes all related data
 * @param workspaceId - The ID of the workspace to delete
 * @param onProgress - Optional callback for progress updates
 * @returns Promise with deletion summary
 */
/**
 * Get collection statistics for a workspace
 * @param workspaceId - The ID of the workspace
 * @returns Promise with collection counts
 */
export const getWorkspaceCollectionStats = async (workspaceId: string): Promise<{
  collections: Array<{
    name: string;
    count: number;
    displayName: string;
  }>;
  totalRecords: number;
}> => {
  const collections = [
    { name: 'apartment_units', displayName: 'Apartment Units' },
    { name: 'assets', displayName: 'Assets' },
    { name: 'attendance', displayName: 'Attendance' },
    { name: 'blocks', displayName: 'Blocks' },
    { name: 'calendar_events', displayName: 'Calendar Events' },
    { name: 'chatThreads', displayName: 'Chat Threads' },
    { name: 'customers', displayName: 'Customers' },
    { name: 'devices', displayName: 'Devices' },
    { name: 'employees', displayName: 'Employees' },
    { name: 'guidelines', displayName: 'Guidelines' },
    { name: 'incidents', displayName: 'Incidents' },
    { name: 'invitations', displayName: 'Invitations' },
    { name: 'leads', displayName: 'Leads' },
    { name: 'leave_requests', displayName: 'Leave Requests' },
    { name: 'lost_and_found', displayName: 'Lost & Found' },
    { name: 'maintenance_requests', displayName: 'Maintenance Requests' },
    { name: 'news', displayName: 'News' },
    { name: 'notifications', displayName: 'Notifications' },
    { name: 'packages', displayName: 'Packages' },
    { name: 'parkings', displayName: 'Parkings' },
    { name: 'payables', displayName: 'Payables' },
    { name: 'payrolls', displayName: 'Payrolls' },
    { name: 'price_plans', displayName: 'Price Plans' },
    { name: 'prospectTags', displayName: 'Prospect Tags' },
    { name: 'receivables', displayName: 'Receivables' },
    { name: 'reservations', displayName: 'Reservations' },
    { name: 'residents', displayName: 'Residents' },
    { name: 'support_tickets', displayName: 'Support Tickets' },
    { name: 'tasks', displayName: 'Tasks' },
    { name: 'users', displayName: 'Users' },
    { name: 'vendors', displayName: 'Vendors' },
    { name: 'visitors', displayName: 'Visitors' }
  ];

  const collectionStats = [];
  let totalRecords = 0;

  for (const collectionInfo of collections) {
    try {
      console.log(`Querying ${collectionInfo.name} for workspace.id == ${workspaceId}`);
      const collectionRef = collection(db, collectionInfo.name);
      
      // First, let's check if there are any records at all in this collection
      const allRecordsQuery = query(collectionRef);
      const allRecordsSnapshot = await getDocs(allRecordsQuery);
      console.log(`Total records in ${collectionInfo.name}: ${allRecordsSnapshot.size}`);
      
      // If there are records, let's see what the workspace field looks like
      if (allRecordsSnapshot.size > 0) {
        const firstDoc = allRecordsSnapshot.docs[0];
        const firstDocData = firstDoc.data();
        console.log(`Sample document from ${collectionInfo.name}:`, {
          id: firstDoc.id,
          workspace: firstDocData.workspace,
          workspace_id: firstDocData.workspace_id
        });
      }
      
      const q = query(collectionRef, where('workspace.id', '==', workspaceId));
      const snapshot = await getDocs(q);
      const count = snapshot.size;
      
      console.log(`Found ${count} records in ${collectionInfo.name} with workspace.id`);
      
      // If no records found with workspace.id, let's also check for workspace_id field
      if (count === 0) {
        console.log(`No records found with workspace.id, checking workspace_id field for ${collectionInfo.name}`);
        const q2 = query(collectionRef, where('workspace_id', '==', workspaceId));
        const snapshot2 = await getDocs(q2);
        const count2 = snapshot2.size;
        console.log(`Found ${count2} records in ${collectionInfo.name} with workspace_id`);
        
        collectionStats.push({
          name: collectionInfo.name,
          count: count2,
          displayName: collectionInfo.displayName
        });
        
        totalRecords += count2;
      } else {
        collectionStats.push({
          name: collectionInfo.name,
          count,
          displayName: collectionInfo.displayName
        });
        
        totalRecords += count;
      }
    } catch (error) {
      console.error(`Error counting ${collectionInfo.name}:`, error);
      collectionStats.push({
        name: collectionInfo.name,
        count: 0,
        displayName: collectionInfo.displayName
      });
    }
  }

  return {
    collections: collectionStats,
    totalRecords
  };
};

export const deleteWorkspaceCompletely = async (
  workspaceId: string,
  onProgress?: (progress: number, status: string) => void
): Promise<{
  success: boolean;
  deletedCounts: Record<string, number>;
  errors: string[];
}> => {
  const deletedCounts: Record<string, number> = {};
  const errors: string[] = [];
  
  // Collections that reference workspace by workspace.id
  const workspaceCollections = [
    'apartment_units',
    'assets', 
    'attendance',
    'blocks',
    'calendar_events',
    'chatThreads',
    'customers',
    'devices',
    'employees',
    'guidelines',
    'incidents',
    'invitations',
    'leads',
    'leave_requests',
    'lost_and_found',
    'maintenance_requests',
    'news',
    'notifications',
    'packages',
    'parkings',
    'payables',
    'payrolls',
    'price_plans',
    'prospectTags',
    'receivables',
    'reservations',
    'residents',
    'support_tickets',
    'tasks',
    'users',
    'vendors',
    'visitors'
  ];

  try {
    console.log(`Starting comprehensive deletion for workspace: ${workspaceId}`);
    onProgress?.(0, 'Starting workspace deletion...');
    
    // Process each collection in batches
    for (let i = 0; i < workspaceCollections.length; i++) {
      const collectionName = workspaceCollections[i];
      const progress = Math.round((i / workspaceCollections.length) * 90); // 90% for collections, 10% for workspace
      onProgress?.(progress, `Processing ${collectionName}...`);
      
      try {
        console.log(`Processing collection: ${collectionName}`);
        
        // Query documents that reference this workspace
        const collectionRef = collection(db, collectionName);
        const q = query(collectionRef, where('workspace.id', '==', workspaceId));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          deletedCounts[collectionName] = 0;
          continue;
        }

        // Process in batches of 500 (Firestore batch limit)
        const batchSize = 500;
        const docs = snapshot.docs;
        let batchCount = 0;
        
        for (let j = 0; j < docs.length; j += batchSize) {
          const batch = writeBatch(db);
          const batchDocs = docs.slice(j, j + batchSize);
          
          batchDocs.forEach(docSnapshot => {
            batch.delete(docSnapshot.ref);
          });
          
          await batch.commit();
          batchCount += batchDocs.length;
          console.log(`Deleted ${batchDocs.length} documents from ${collectionName} (batch ${Math.floor(j / batchSize) + 1})`);
        }
        
        deletedCounts[collectionName] = batchCount;
        console.log(`Completed ${collectionName}: ${batchCount} documents deleted`);
        
      } catch (collectionError: any) {
        const errorMsg = `Failed to delete from ${collectionName}: ${collectionError.message}`;
        console.error(errorMsg, collectionError);
        errors.push(errorMsg);
        deletedCounts[collectionName] = 0;
      }
    }

    // Finally, delete the workspace document itself
    onProgress?.(95, 'Deleting workspace document...');
    try {
      const workspaceRef = doc(db, 'workspaces', workspaceId);
      await deleteDoc(workspaceRef);
      deletedCounts['workspaces'] = 1;
      console.log('Workspace document deleted successfully');
    } catch (workspaceError: any) {
      const errorMsg = `Failed to delete workspace document: ${workspaceError.message}`;
      console.error(errorMsg, workspaceError);
      errors.push(errorMsg);
    }
    
    onProgress?.(100, 'Deletion completed!');

    const totalDeleted = Object.values(deletedCounts).reduce((sum, count) => sum + count, 0);
    console.log(`Workspace deletion completed. Total documents deleted: ${totalDeleted}`);
    console.log('Deletion summary:', deletedCounts);
    
    if (errors.length > 0) {
      console.warn('Deletion completed with errors:', errors);
    }

    return {
      success: errors.length === 0,
      deletedCounts,
      errors
    };

  } catch (error: any) {
    console.error('Critical error during workspace deletion:', error);
    return {
      success: false,
      deletedCounts,
      errors: [...errors, `Critical error: ${error.message}`]
    };
  }
}; 